import React, {FormEvent, ChangeEvent, useRef, useEffect} from 'react'

import {Flex} from 'src/components/Box'
import {Action} from 'src/components/Action'
import * as styles from 'src/styles'
import {SearchIcon} from 'src/components/icons'

interface SearchFormProps {
  query: string
  setQuery: (value: string) => void
  performSearch: () => void
  disabled?: boolean
  autofocus?: boolean
  placeholder?: string
  ariaLabel?: string
}

const SearchForm = ({
  query,
  setQuery,
  performSearch,
  disabled = false,
  autofocus = false,
  placeholder = 'Find content',
  ariaLabel = 'Find content',
}: SearchFormProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (autofocus) {
      inputRef?.current && inputRef.current.focus()
    }
  }, [autofocus])

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) =>
    setQuery(event.target.value)

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    performSearch()
    inputRef?.current && inputRef.current.blur()
  }

  return (
    <form onSubmit={handleSearch} css={{flex: 1}}>
      <Flex alignItems="center">
        <input
          ref={inputRef}
          name="query"
          autoComplete="off"
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          aria-label={ariaLabel}
          css={(theme) => ({
            flex: 1,
            padding: theme.space[1],
            ...styles.borders.simpleBorder(2),
            borderColor: theme.colors.grey2,
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
            ...styles.typography.body,
            '&:focus': {
              outline: 'none',
              borderColor: theme.colors.green,
            },
          })}
        />
        <Action
          aria-label="Search content"
          disabled={disabled}
          type="submit"
          css={(theme) => ({
            background: theme.colors.grey2,
            padding: `${theme.space[1]} ${theme.space[2]}`,
            minWidth: 'initial',
            ...styles.borders.simpleBorder(2),
            borderTopRightRadius: 10,
            borderBottomRightRadius: 10,
            ':enabled': {
              borderColor: theme.colors.green,
            },
            marginLeft: '-2px',
          })}
        >
          <SearchIcon height="1.33rem" aria-label="Search" />
        </Action>
      </Flex>
    </form>
  )
}

export default SearchForm
